import { supabase } from "@/integrations/supabase/client";

/**
 * Deduct manual inventory for menu items when order is marked as preparing
 */
export const deductManualInventoryForOrder = async (
  orderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`Deducting manual inventory for order: ${orderId}`);

    // Get order items for this order
    const { data: orderItems, error: orderError } = await supabase
      .from("order_items")
      .select("menu_item_id, quantity")
      .eq("order_id", orderId);

    if (orderError) {
      console.error("Error fetching order items:", orderError);
      return { success: false, error: orderError.message };
    }

    if (!orderItems || orderItems.length === 0) {
      return { success: true }; // No items to process
    }

    // For each order item, find and deduct manual inventory
    for (const item of orderItems) {
      if (!item.menu_item_id) continue;

      // Get manual inventory relationships for this menu item
      const { data: inventoryRelations, error: relError } = await supabase
        .from("menu_item_inventory")
        .select(
          `
          inventory_item_id,
          quantity_required,
          inventory_items!inner (
            id,
            current_quantity,
            inventory_type
          )
        `
        )
        .eq("menu_item_id", item.menu_item_id)
        .eq("inventory_items.inventory_type", "manual");

      if (relError) {
        console.error(
          `Error fetching inventory relations for menu item ${item.menu_item_id}:`,
          relError
        );
        continue;
      }

      // Deduct inventory for each manual inventory item
      for (const relation of inventoryRelations || []) {
        const deductAmount = relation.quantity_required * item.quantity;
        const newQuantity = Math.max(
          0,
          relation.inventory_items.current_quantity - deductAmount
        );

        const { error: updateError } = await supabase
          .from("inventory_items")
          .update({
            current_quantity: newQuantity,
            updated_at: new Date().toISOString(),
          })
          .eq("id", relation.inventory_item_id);

        if (updateError) {
          console.error(
            `Error updating inventory item ${relation.inventory_item_id}:`,
            updateError
          );
        } else {
          console.log(
            `Deducted ${deductAmount} from inventory item ${relation.inventory_item_id}, new quantity: ${newQuantity}`
          );
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error in deductManualInventoryForOrder:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Add back manual inventory for menu items when order is cancelled (if it was previously prepared)
 */
export const addBackManualInventoryForOrder = async (
  orderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`Adding back manual inventory for order: ${orderId}`);

    // Get order items for this order
    const { data: orderItems, error: orderError } = await supabase
      .from("order_items")
      .select("menu_item_id, quantity")
      .eq("order_id", orderId);

    if (orderError) {
      console.error("Error fetching order items:", orderError);
      return { success: false, error: orderError.message };
    }

    if (!orderItems || orderItems.length === 0) {
      return { success: true }; // No items to process
    }

    // For each order item, find and add back manual inventory
    for (const item of orderItems) {
      if (!item.menu_item_id) continue;

      // Get manual inventory relationships for this menu item
      const { data: inventoryRelations, error: relError } = await supabase
        .from("menu_item_inventory")
        .select(
          `
          inventory_item_id,
          quantity_required,
          inventory_items!inner (
            id,
            current_quantity,
            inventory_type
          )
        `
        )
        .eq("menu_item_id", item.menu_item_id)
        .eq("inventory_items.inventory_type", "manual");

      if (relError) {
        console.error(
          `Error fetching inventory relations for menu item ${item.menu_item_id}:`,
          relError
        );
        continue;
      }

      // Add back inventory for each manual inventory item
      for (const relation of inventoryRelations || []) {
        const addBackAmount = relation.quantity_required * item.quantity;
        const newQuantity =
          relation.inventory_items.current_quantity + addBackAmount;

        const { error: updateError } = await supabase
          .from("inventory_items")
          .update({
            current_quantity: newQuantity,
            updated_at: new Date().toISOString(),
          })
          .eq("id", relation.inventory_item_id);

        if (updateError) {
          console.error(
            `Error updating inventory item ${relation.inventory_item_id}:`,
            updateError
          );
        } else {
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error in addBackManualInventoryForOrder:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
